import React, { useEffect, useState } from 'react';
import BrandLogo from './BrandLogo';
import { useVendor } from '../context/VendorContext';
import { api } from '../services/api';
import { primePedidoStatusSound } from '../utils/pedidoStatusSound';

const REMEMBER_USER_KEY = 'oh_vendedor_remember_user';

export default function VendorGate({ children }) {
  const { vendedor, loading, locked, login } = useVendor();
  const [username, setUsername] = useState(() => {
    try {
      return localStorage.getItem(REMEMBER_USER_KEY) || '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState('');
  const [rememberUser, setRememberUser] = useState(() => {
    try {
      return Boolean(localStorage.getItem(REMEMBER_USER_KEY));
    } catch {
      return false;
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [backendOk, setBackendOk] = useState(null);
  const [welcomeName, setWelcomeName] = useState('');

  useEffect(() => {
    if (vendedor || locked) return undefined;
    let cancelled = false;
    let attempt = 0;
    function check() {
      attempt += 1;
      api
        .ping()
        .then(() => {
          if (!cancelled) setBackendOk(true);
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) {
            setTimeout(check, 1200 * attempt);
          } else {
            setBackendOk(false);
          }
        });
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [vendedor, locked]);

  useEffect(() => {
    if (!welcomeName) return undefined;
    const t = setTimeout(() => setWelcomeName(''), 1600);
    return () => clearTimeout(t);
  }, [welcomeName]);

  if (loading) {
    return (
      <div className="pin-screen">
        <div className="pin-screen-glow" aria-hidden="true" />
        <p className="pin-loading">Preparando…</p>
      </div>
    );
  }

  if (welcomeName) {
    return (
      <div className="pin-screen">
        <div className="pin-screen-glow" aria-hidden="true" />
        <div className="pin-card pin-welcome">
          <BrandLogo size="lg" />
          <p className="pin-kicker">¡Oh! Belleza</p>
          <h1>Hola, {welcomeName}</h1>
          <p className="pin-sub">Abriendo tu panel de pedidos…</p>
        </div>
      </div>
    );
  }

  if (vendedor) return children;

  if (locked) {
    return (
      <div className="pin-screen">
        <div className="pin-screen-glow" aria-hidden="true" />
        <div className="pin-card">
          <BrandLogo size="lg" />
          <p className="pin-kicker">¡Oh! Belleza</p>
          <h1>App bloqueada</h1>
          <p className="pin-sub">Esta instalación está fija a un vendedor.</p>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // Re-pinga antes de login: a veces el 1er intento falló por un blip corto.
      try {
        await api.ping();
        setBackendOk(true);
      } catch {
        /* login igual puede funcionar si el ping flaqueó */
      }
      primePedidoStatusSound();
      const data = await login(username, password);
      try {
        if (rememberUser) localStorage.setItem(REMEMBER_USER_KEY, String(username || '').trim());
        else localStorage.removeItem(REMEMBER_USER_KEY);
      } catch {
        /* ignore */
      }
      setWelcomeName(data?.vendedor?.nombre || 'vendedor');
      if (!rememberUser) setUsername('');
      setPassword('');
    } catch (err) {
      setError(err.message || 'Usuario o contraseña incorrectos');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pin-screen">
      <div className="pin-screen-glow" aria-hidden="true" />
      <div className="pin-card">
        <BrandLogo size="lg" />
        <p className="pin-kicker">¡Oh! Belleza</p>
        <h1>Vendedor</h1>
        <p className="pin-sub">Inicia sesión para tomar pedidos</p>
        <span className="pin-role">Campo</span>
        {backendOk === false && !error ? (
          <p className="error backend-warn">
            No hay respuesta del API ahora. Revisa WiFi y en cPanel que Node esté{' '}
            <strong>Running</strong>. Luego pulsa Entrar de nuevo.
          </p>
        ) : null}
        <form className="pin-form" onSubmit={submit}>
          <label className="pin-label" htmlFor="vendedor-username">
            Usuario
          </label>
          <div className="pin-input-wrap">
            <input
              id="vendedor-username"
              className="pin-input-text"
              type="text"
              autoComplete="username"
              placeholder="usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <label className="pin-label" htmlFor="vendedor-password">
            Contraseña
          </label>
          <div className="pin-input-wrap">
            <input
              id="vendedor-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="pin-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? 'Ocultar' : 'Ver'}
            </button>
          </div>
          {error ? <p className="error">{error}</p> : null}
          <label className="pin-remember">
            <input
              type="checkbox"
              checked={rememberUser}
              onChange={(e) => setRememberUser(e.target.checked)}
            />
            Recordar usuario en esta tablet
          </label>
          <button
            type="submit"
            className="pin-submit"
            disabled={submitting || !username.trim() || !password}
          >
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
        <p className="pin-hint">Si no tienes acceso, pídelo en oficina / admin.</p>
      </div>
    </div>
  );
}
